import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NavBar from "../../components/NavBar/NavBar";
import styles from "./ViewProfilePage.module.css";
import pfp_default from "../../assets/pfp_default.png";
import { BASE_URL } from "../../App";

export default function ViewProfilePage() {
    const { user_id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [flash, setFlash] = useState({ type: "", msg: "" });
    const [profile, setProfile] = useState(null);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let flashTimeout = null; // Variable to hold the timeout ID

        async function load() {
            setLoading(true);
            setFlash({ type: "", msg: "" });

            try {
                const res = await fetch(
                    `${BASE_URL}/backend/display_profile.php?user_id=${encodeURIComponent(user_id)}`,
                    { method: "GET", credentials: "include", cache: "no-store" }
                );

                const json = await res.json();
                if (cancelled) return;

                if (json?.success && json?.profile) {
                    setProfile(json.profile);
                } else {
                    const errorMsg = json?.error || "Could not load profile. Please try again later.";
                    setFlash({
                        type: "error",
                        msg: errorMsg,
                    });
                    
                    // Set a timeout to clear the error message after 5000ms (5 seconds)
                    flashTimeout = setTimeout(() => {
                        if (!cancelled) {
                            setFlash({ type: "", msg: "" });
                        }
                    }, 5000);
                }
            } catch {
                if (!cancelled) {
                    const errorMsg = "Could not load profile. Please try again later.";
                    setFlash({ type: "error", msg: errorMsg });

                    // Set a timeout to clear the error message after 5000ms (5 seconds)
                    flashTimeout = setTimeout(() => {
                        if (!cancelled) {
                            setFlash({ type: "", msg: "" });
                        }
                    }, 5000);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        
        // Cleanup function to clear the cancelled flag and the timeout
        return () => { 
            cancelled = true; 
            if (flashTimeout) {
                clearTimeout(flashTimeout);
            }
        };
    }, [user_id]);

    const avatarSrc = profile?.avatar_path
        ? `${BASE_URL}/backend/${profile.avatar_path}`
        : pfp_default;

    const onMessageMe = () => {
        navigate(`/messages/new/${user_id}`);
    };

    const toBool = v => v === true || v === 1 || v === "1";

    return (
        <div className={styles.page}>
            <NavBar />
            <main className={styles.outerContainer}>
                <div className={styles.innerContainer}>
                    {loading && <div className={styles.details}>Loading…</div>}

                    {!loading && flash.type === "error" && (
                        <div className={styles.alertError}>
                            {flash.msg}
                        </div>
                    )}

                    {!loading && profile && (
                        <>
                            {/* Profile Picture */}
                            <img src={avatarSrc} alt="Profile" className={styles.avatar} />

                            {/* Username Display */}
                            <h1 className={styles.name}>
                                @{profile?.username || "unknown"}
                            </h1>

                            {/* Address + Roommates */}
                            <p className={styles.details}>
                                Resident of {profile?.address ?? "—"}
                            </p>
                            <p className={styles.details}>
                                Roommate of {profile?.roommates ?? "—"}
                            </p>

                            {/* Basic Info */}
                            <div className={styles.section}>
                                <h2 className={styles.sectionTitle}>Basic Information</h2>
                                <ul className={styles.profileList}>
                                    <li><b>Full Name:</b> {profile?.name || "—"}</li>
                                    <li><b>Username:</b> @{profile?.username || "—"}</li>
                                    <li><b>Gender:</b> {profile?.gender || "—"}</li>
                                    <li><b>Age:</b> {profile?.age ?? "—"}</li>
                                    <li><b>Major/Occupation:</b> {profile?.occupation || "—"}</li>
                                </ul>
                            </div>

                            {/* About Me */}
                            <div className={styles.section}>
                                <h2 className={styles.sectionTitle}>About Me</h2>
                                <p className={styles.about}>{profile?.about || "—"}</p>
                            </div>

                            {/* Preferences */}
                            <div className={styles.section}>
                                <h2 className={styles.sectionTitle}>Preferences</h2>
                                <div className={styles.preferences}>
                                    <div
                                        className={`${styles.prefBox} ${
                                            !toBool(profile?.pet_friendly) ? styles.prefBoxOff : ""
                                        }`}
                                    >
                                        {toBool(profile?.pet_friendly) ? "Pet Friendly" : "Not Pet-Friendly"}
                                    </div>

                                    <div
                                        className={`${styles.prefBox} ${
                                            !toBool(profile?.no_smoking) ? styles.prefBoxOff : ""
                                        }`}
                                    >
                                        {toBool(profile?.no_smoking) ? "No Smoking" : "Not Smoke-Friendly"}
                                    </div>
                                </div>
                            </div>

                            {/* Message Button */}
                            <div className={styles.buttons}>
                                <button
                                    className={styles.messageBtn}
                                    onClick={onMessageMe}
                                    disabled={sending}
                                >
                                    {sending ? "Opening…" : "Message Me!"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}